import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useBranding } from '../hooks/useBranding';
import ProfileCompletionBanner from './ProfileCompletionBanner';
import { LanguageSwitcher } from './ui';

export default function Layout() {
  const { t } = useTranslation();
  const { signOut, userProfile } = useAuth();
  const { branding } = useBranding();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const headerStyle = branding?.primaryColor
    ? { backgroundColor: branding.primaryColor }
    : undefined;

  const navigate = useNavigate();
  const handleSignOut = async () => {
    if (window.confirm('Are you sure you want to log out?')) {
      // Първо към входа, после изход: иначе `PrivateRoute` праща на `/login` с `from` = тази
      // страница и следващият влязъл (друг родител на същото устройство) попада тук.
      navigate('/login', { replace: true });
      await signOut();
    }
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${
      isActive
        ? 'bg-white text-primary-700'
        : 'text-primary-100 hover:bg-primary-600'
    }`;

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      {/* Header */}
      <header
        className="sticky top-0 z-50 shadow-sm border-b border-primary-800"
        style={headerStyle || { backgroundColor: '#1d4ed8' }}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 gap-2">
            {/* Logo */}
            <div className="flex items-center shrink-0">
              {branding?.logoUrl && (
                <img
                  src={branding.logoUrl}
                  alt="Logo"
                  className="h-9 w-auto max-w-[100px] object-contain"
                />
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-white shrink-0"
              aria-label="Menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-2">
              <nav className="flex flex-nowrap gap-0.5 items-center">
                <NavLink to="/" end className={navLinkClass}>
                  {t('nav.dashboard')}
                </NavLink>
                <NavLink to="/assignments" className={navLinkClass}>
                  {t('nav.assignments')}
                </NavLink>
                <NavLink to="/children" className={navLinkClass}>
                  {t('nav.children')}
                </NavLink>
                <NavLink to="/fees" className={navLinkClass}>
                  {t('nav.fees')}
                </NavLink>
                <NavLink to="/volunteer" className={navLinkClass}>
                  {t('nav.volunteer')}
                </NavLink>
                <NavLink to="/profile" className={navLinkClass}>
                  {t('nav.profile')}
                </NavLink>
                <NavLink to="/faq" className={navLinkClass}>
                  {t('nav.faq')}
                </NavLink>
              </nav>

              <div className="flex items-center gap-2 shrink-0">
                {/* Езикът е ТУК, а не в настройките: родител, озовал се на
                    непознат език, не бива да търси превключвателя в меню, което
                    не може да прочете. */}
                <LanguageSwitcher />
                <span className="text-sm text-primary-100 hidden xl:block">
                  {userProfile?.displayName || userProfile?.email}
                </span>
                <button
                  onClick={handleSignOut}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-primary-700 bg-white hover:bg-gray-100 transition-colors"
                >
                  {t('nav.logout')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div
            className="lg:hidden border-t"
            style={{
              backgroundColor: branding?.primaryColor || '#1d4ed8',
              borderColor: 'rgba(255,255,255,0.2)',
            }}
          >
            <nav className="px-4 py-3 space-y-1">
              {[
                { to: '/', label: t('nav.dashboard'), end: true },
                { to: '/assignments', label: t('nav.assignments') },
                { to: '/children', label: t('nav.children') },
                { to: '/fees', label: t('nav.fees') },
                { to: '/volunteer', label: t('nav.volunteer') },
                { to: '/profile', label: t('nav.profile') },
                { to: '/faq', label: t('nav.faq') },
              ].map(({ to, label, end }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={end}
                  onClick={() => setMobileMenuOpen(false)}
                  className={navLinkClass}
                >
                  {label}
                </NavLink>
              ))}
            </nav>

            <div
              className="px-4 py-3 border-t"
              style={{ borderColor: 'rgba(255,255,255,0.2)' }}
            >
              <span className="block px-3 py-2 text-sm text-primary-100">
                {userProfile?.displayName || userProfile?.email}
              </span>
              <button
                onClick={() => { setMobileMenuOpen(false); handleSignOut(); }}
                className="w-full text-left px-3 py-2 rounded-lg text-sm font-medium text-primary-100 hover:bg-primary-600 transition-colors mt-1"
              >
                {t('nav.logout')}
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Profile completion banner — shown when family.metadata.profileComplete !== true (PR #4) */}
      <ProfileCompletionBanner />

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Outlet />
      </main>
    </div>
  );
}
